import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import * as styles from './Tax.module.scss'
import { getTaxVariant } from './utils'

import { Button } from '../../basics/Button'
import { BaseText as Text } from '../../basics/Text'
import { TaxModal } from '../../modals/TaxModal'
import { Panel } from '../../sidebars/Panel'

type TaxProps = {
  className?: string
  collectAccommodationTaxOnSite: boolean
  collectBathTaxOnSite: boolean
}

const titleKeyByVariant = {
  accommodation: 'accommodationTaxTitle',
  bath: 'bathingTaxTitle',
  both: 'accommodationAndBathingTaxTitle',
} as const

const buttonKeyByVariant = {
  accommodation: 'accommodationTaxBtn',
  bath: 'bathingTaxBtn',
  both: 'accommodationAndBathingTaxBtn',
} as const

export const Tax: React.FC<TaxProps> = ({
  className,
  collectAccommodationTaxOnSite,
  collectBathTaxOnSite,
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'components.Tax',
  })
  const [isOpen, setIsOpen] = useState(false)
  const variant = getTaxVariant(
    collectAccommodationTaxOnSite,
    collectBathTaxOnSite
  )

  const title = useMemo(() => {
    if (!variant) {
      return ''
    }

    return t(titleKeyByVariant[variant])
  }, [t, variant])

  const buttonLabel = useMemo(() => {
    if (!variant) {
      return ''
    }

    return t(buttonKeyByVariant[variant])
  }, [t, variant])

  if (!variant) {
    return null
  }

  return (
    <section className={clsx(styles.tax, className)}>
      <Panel
        items={[
          <dl>
            <Text size={18} weight="bold">
              {title}
            </Text>
          </dl>,
          <div className={styles.body}>
            {collectAccommodationTaxOnSite && <p>{t('accommodationTax')}</p>}
            {collectBathTaxOnSite && <p>{t('bathingTax')}</p>}
            <Button
              htmlType="button"
              variant="secondary"
              outline
              size="small"
              className={styles.button}
              onClick={() => {
                setIsOpen(true)
              }}
            >
              {buttonLabel}
            </Button>
          </div>,
        ]}
      />
      <TaxModal
        isOpen={isOpen}
        showAccommodationTax={collectAccommodationTaxOnSite}
        showBathTax={collectBathTaxOnSite}
        onClose={() => {
          setIsOpen(false)
        }}
      />
    </section>
  )
}
